import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaEye, FaEyeSlash } from 'react-icons/fa'; // 🎯 Added eye visibility icons
import axios from 'axios';
import Header1 from '../components/Header1'; 
import Footer from '../components/Footer'; 
import './Signup.css'; 

const Signup = () => {
    const [formData, setFormData] = useState({ username: '', email: '', password: '' });
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false); // 🎯 Tracks eye toggle visibility
    const [passwordStrength, setPasswordStrength] = useState({ score: 0, text: '', color: '' });
    
    const navigate = useNavigate();
    const API_URL = process.env.REACT_APP_API_URL;

    // 🎯 Password Strength Evaluator Logic
    const checkPasswordStrength = (password) => {
        if (!password) {
            return { score: 0, text: '', color: '' };
        }
        
        let score = 0;
        if (password.length >= 8) score++;
        if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score++;
        if (/[0-9]/.test(password)) score++;
        if (/[^A-Za-z0-9]/.test(password)) score++;

        switch (score) {
            case 1:
                return { score, text: 'Weak (Add numbers/symbols)', color: '#ef4444' };
            case 2:
            case 3:
                return { score, text: 'Medium (Mix in capitals/symbols)', color: '#f59e0b' };
            case 4:
                return { score, text: 'Strong Secure Footprint', color: '#10b981' };
            default:
                return { score, text: 'Too Short (Min 8 chars)', color: '#ef4444' };
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
        if (error) setError('');

        // 🎯 Compute real-time evaluation if user shifts the password field
        if (name === 'password') {
            setPasswordStrength(checkPasswordStrength(value));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // 🎯 Block submission if password fails standard requirements
        if (passwordStrength.score < 3) {
            setError('Please verify your password meets secure criteria (min 8 characters mixing alphanumeric & symbol metrics).');
            return;
        }

        try {
            const res = await axios.post(`${API_URL}/api/auth/signup`, formData);
            
            if (res.status === 201) {
                if (res.data.verificationToken) {
                    sessionStorage.setItem('temp_verify_token', res.data.verificationToken);
                }
                navigate('/verify-code'); 
            }
        } catch (error) {
            console.error('Signup rejection:', error);
            if (error.response && error.response.data && error.response.data.message) {
                setError(error.response.data.message);
            } else {
                setError('An error occurred. Please check your network connection.');
            }
        }
    };

    return (
        <>
            <Header1 />
            <div className="signup-container">
                <form onSubmit={handleSubmit}>
                    <h2>Sign Up</h2>
                    {error && <div className="error-message">{error}</div>}
                    
                    <div className="input-group">
                        <input
                            type="text"
                            name="username"
                            placeholder="Username"
                            value={formData.username}
                            onChange={handleChange}
                            required
                        />
                    </div>

                    <div className="input-group">
                        <input
                            type="email"
                            name="email"
                            placeholder="Email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                        />
                    </div>

                    {/* 🎯 CHANGED: Using a clean, unique container name specifically for this layout */}
                    <div className="password-input-group">
                        <input
                            type={showPassword ? 'text' : 'password'}
                            name="password"
                            placeholder="Password"
                            value={formData.password}
                            onChange={handleChange}
                            required
                        />
                        <button 
                            type="button" 
                            className="password-toggle-btn"
                            onClick={() => setShowPassword(!showPassword)}
                            tabIndex="-1"
                        >
                            {showPassword ? <FaEyeSlash /> : <FaEye />}
                        </button>
                    </div>

                    {/* 🎯 Real-time visual layout tracker meter */}
                    {formData.password && (
                        <div className="password-strength-tracker">
                            <div className="strength-bar-container">
                                <div 
                                    className="strength-bar-filler" 
                                    style={{ 
                                        width: `${(passwordStrength.score / 4) * 100}%`, 
                                        backgroundColor: passwordStrength.color 
                                    }}
                                />
                            </div>
                            <span className="strength-text" style={{ color: passwordStrength.color }}>
                                {passwordStrength.text}
                            </span>
                        </div>
                    )}

                    <button type="submit" disabled={passwordStrength.score < 3}>Sign Up</button>
                    <p>Already have an account? <a href="/login">Login</a></p>
                </form>
            </div>
            <Footer />
        </>
    );
};

export default Signup;